import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import CreatableSelect from 'react-select/creatable';
import { FormGroup, Label } from 'reactstrap';

import { MediaImage, resizeImage } from '@techchange/filestack';
import { FormField, FIELD_TYPES } from '@techchange/components';
import { Button } from '@techchange/components';
import { messages } from '../../../i18n';

import styles from './TemplateSignatureForm.module.scss';

const propTypes = {

	/**
	 * The template signature.
	 */
	initialValues: PropTypes.shape({

		/**
		 * The certificate person's ID.
		 */
		id: PropTypes.string,

		/**
		 * The ID of the associated person.
		 */
		person_id: PropTypes.string.isRequired,

		/**
		 * The person's name.
		 */
		name: PropTypes.string.isRequired,

		/**
		 * The person's affiliation (i.e. occupation/role).
		 */
		affiliation: PropTypes.string.isRequired,

		/**
		 * The person's signature.
		 */
		signature: PropTypes.string,

	}),

	/**
	 * The list of all available people for this organization.
	 */
	people: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The person's ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The person's name.
		 */
		name: PropTypes.string.isRequired,

		/**
		 * The person's affiliation (i.e. occupation/role).
		 */
		affiliation: PropTypes.string.isRequired,

		/**
		 * The person's signature.
		 */
		signature: PropTypes.string,

	})).isRequired,

	/**
	 * The filestack credentials object.
	 */
	filestack: PropTypes.shape({

		apiKey: PropTypes.string.isRequired,

		security: PropTypes.shape({

			policy: PropTypes.string.isRequired,

			signature: PropTypes.string.isRequired,

		}).isRequired,

	}).isRequired,

	/**
	 * Callback to handle adding a new signature to the template.
	 */
	onAdd: PropTypes.func.isRequired,

	/**
	 * Callback to handle creating a new organization person and
	 * adding their signature to the template.
	 */
	onCreate: PropTypes.func.isRequired,

	/**
	 * Callback function to exit out of form.
	 */
	onCancel: PropTypes.func.isRequired,

};

const TemplateSignatureForm = ({ initialValues, people, filestack, onAdd, onCreate, onCancel }) => {

	const intl = useIntl();

	const [formData, setFormData] = useState({
		id: (initialValues && initialValues.person_id),
		name: (initialValues && initialValues.name) || '',
		affiliation: (initialValues && initialValues.affiliation) || '',
		signature: (initialValues && initialValues.signature),
	});

	const [inputValidation, setInputValidation] = useState({
		missingName: false,
		missingAffiliation: false,
	});

	const newPerson = Boolean(!initialValues);

	const handleInputChange = (name, value) => {

		setFormData((prev) => Object.assign({}, prev, { [name]: value }));

		// The person's name is a required field, so re-calculate the input validation
		if (name === 'name') {

			setInputValidation((prevState) => Object.assign({}, prevState, {
				missingName: !value,
			}));

		}

		// The person's affiliation is a required field, so re-calculate the input validation
		if (name === 'affiliation') {

			setInputValidation((prevState) => Object.assign({}, prevState, {
				missingAffiliation: !value,
			}));

		}

	};

	const handleOnSelectChange = (person) => {

		setFormData((prev) => Object.assign({}, prev, {
			id: person.id,
			name: person.name,
			affiliation: person.affiliation,
			signature: person.signature,
		}));

		if (person.id) {
			onAdd(person);
		}

	};

	const handleOnSave = () => {

		// Validate form data
		const updatedInputValidation = {};

		if (!formData.name) {
			updatedInputValidation.missingName = true;
		}

		if (!formData.affiliation) {
			updatedInputValidation.missingAffiliation = true;
		}

		// Check to see if input validation failed...
		if (Object.keys(updatedInputValidation).length > 0) {

			setInputValidation((prevState) => Object.assign({}, prevState, updatedInputValidation));
			return;

		}

		if (newPerson) {

			onCreate(formData);

		} else {

			onAdd(formData);

		}

	};

	const renderOption = (optionProps) => {

		const { innerRef, innerProps, data } = optionProps;

		if (data.__isNew__) {
			return (
				<div
					ref={optionProps.innerRef}
					className="template-signature-option"
					{...optionProps.innerProps}>
					<b>
						<i className="fal fa-circle-plus me-1" />
						<FormattedMessage
							{...messages.TC_COMPONENTS_CERTIFICATE_EDITOR_PERSON_SELECT_NEW_PROMPT} />
					</b>
				</div>
			);
		}

		return (
			<div
				ref={innerRef}
				{...innerProps}>
				<div className="template-signature-option">
					<b>{data.name}</b>
					<div className="template-signature-affiliation">{data.affiliation}</div>
				</div>
			</div>
		);

	};

	return (
		<div className="template-signature-form">
			<FormGroup className="mb-5">
				{newPerson && !formData.name &&
					<Fragment>
						<CreatableSelect
							className={styles['certificate-template-form-signatures-person-select']}
							classNamePrefix="select"
							placeholder={intl.formatMessage(messages.TC_COMPONENTS_CERTIFICATE_EDITOR_SELECT_PLACEHOLDER_PERSON)}
							components={{
								Option: renderOption,
							}}
							options={people.map((person) => Object.assign({}, person, {
								label: person.name,
								value: person.id,
							}))}
							// eslint-disable-next-line jsx-a11y/no-autofocus
							autoFocus
							onBlur={onCancel}
							onChange={(person) => handleOnSelectChange(person)}
							onCreateOption={(inputValue) => {

								// Initialize the form data with the new person's name
								setFormData((prev) => Object.assign({}, prev, { name: inputValue }));

								// Reset the input validation
								setInputValidation((prev) => Object.assign({}, prev, {
									missingName: false,
									missingAffiliation: false,
								}));

							}} />
					</Fragment>
				}
				{(!newPerson || formData.name) &&
					<FormField
						type={FIELD_TYPES.TEXT}
						name="name"
						value={formData.name}
						label={intl.formatMessage(messages.TC_COMPONENTS_CERTIFICATE_EDITOR_PERSON_FIELD_LABEL_NAME)}
						placeholder={intl.formatMessage(messages.TC_COMPONENTS_CERTIFICATE_EDITOR_PERSON_FIELD_LABEL_NAME)}
						isInvalid={inputValidation.missingName}
						isEditing
						isRequired
						onChange={handleInputChange} />
				}
			</FormGroup>
			{(!newPerson || formData.name) &&
				<Fragment>
					<FormField
						type={FIELD_TYPES.TEXT}
						name="affiliation"
						value={formData.affiliation}
						label={intl.formatMessage(messages.TC_COMPONENTS_CERTIFICATE_EDITOR_PERSON_FIELD_LABEL_AFFILIATION)}
						placeholder={intl.formatMessage(messages.TC_COMPONENTS_CERTIFICATE_EDITOR_PERSON_FIELD_LABEL_AFFILIATION)}
						isInvalid={inputValidation.missingAffiliation}
						isEditing
						isRequired
						onChange={handleInputChange} />
					<FormGroup className="mb-5">
						<Label for="signature">
							<FormattedMessage
								{...messages.TC_COMPONENTS_CERTIFICATE_EDITOR_PERSON_FIELD_LABEL_SIGNATURE} />
						</Label>
						<div className="template-signature-img mb-1">
							{formData.signature &&
								<img
									alt={intl.formatMessage(messages.TC_COMPONENTS_CERTIFICATE_EDITOR_PERSON_FIELD_LABEL_SIGNATURE)}
									src={resizeImage(formData.signature, 'height', 50)} />
							}
						</div>
						<div className="d-flex">
							<MediaImage
								filestack={filestack}
								onSuccess={(url) => handleInputChange('signature', url)}>
								<Button
									color="neutral"
									outline>
									{formData.signature
										? <FormattedMessage {...messages.TC_COMPONENTS_IMAGE_PICKER_BUTTON_TEXT_CHANGE} />
										: <FormattedMessage {...messages.TC_COMPONENTS_IMAGE_PICKER_BUTTON_TEXT_UPLOAD} />
									}
								</Button>
							</MediaImage>
							{formData.signature &&
								<div className="ms-2">
									<Button
										color="outline-danger"
										onClick={() => handleInputChange('signature', null)}>
										<FormattedMessage {...messages.TC_COMPONENTS_IMAGE_PICKER_BUTTON_TEXT_REMOVE} />
									</Button>
								</div>
							}
						</div>
					</FormGroup>
					<Button
						color="primary"
						onClick={handleOnSave}>
						<FormattedMessage {...messages.TC_COMPONENTS_GENERAL_SAVE_LABEL} />
					</Button>
					<span className="ms-2">
						<Button
							color="neutral"
							outline
							onClick={onCancel}>
							<FormattedMessage {...messages.TC_COMPONENTS_GENERAL_CANCEL_LABEL} />
						</Button>
					</span>
				</Fragment>
			}
		</div>
	);

};

TemplateSignatureForm.propTypes = propTypes;

export default TemplateSignatureForm;
